import { create } from 'zustand';

import type { PetAccessoryOffset } from '@shared/schemas/room.js';

import { roomApi } from '../api/endpoints.js';

/**
 * Độ lệch (dx, dy) của các món ĐANG MẶC — nguồn dữ liệu phía client.
 *
 * ⭐ VÌ SAO LÀ STORE RIÊNG, KHÔNG NHÉT VÀO `roomPlacementStore`:
 *   Hai thứ này có vòng đời khác nhau. `placements` là "món nằm ở đâu trong phòng" và được ghi
 *   theo cả bộ mỗi lần kéo đồ; `offsets` là "món lệch bao nhiêu so với công thức neo TRÊN NGƯỜI
 *   con vật" và chỉ đổi khi bé kéo một món đang mặc. Gộp chung thì mỗi lần kéo mũ phải gửi lại
 *   toàn bộ vị trí đồ đạc trong phòng (và ngược lại) — hai request đua nhau ghi đè lẫn nhau.
 *
 * ⚠️ Cùng khuôn với `roomPlacementStore`: `childId` đi kèm dữ liệu để một response về muộn của
 *    bé TRƯỚC không ghi đè lên trạng thái của bé ĐANG chọn (đổi bé giữa lúc đang tải).
 */
interface PetAccessoryOffsetState {
  childId: string | null;
  offsets: PetAccessoryOffset[] | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  load: (childId: string) => Promise<void>;
  save: (childId: string, offsets: PetAccessoryOffset[]) => Promise<void>;
  reset: () => void;
}

function emptyState(): Pick<
  PetAccessoryOffsetState,
  'childId' | 'offsets' | 'loading' | 'saving' | 'error'
> {
  return { childId: null, offsets: null, loading: false, saving: false, error: null };
}

export const usePetAccessoryOffsetStore = create<PetAccessoryOffsetState>((set, get) => ({
  ...emptyState(),
  load: async (childId) => {
    if (get().childId === childId && get().loading) return;
    set({ ...emptyState(), childId, loading: true });
    try {
      const offsets = await roomApi.getAccessoryOffsets(childId);
      if (get().childId !== childId) return;
      set({ offsets, loading: false, error: null });
    } catch (error) {
      if (get().childId !== childId) return;
      set({ loading: false, error: error instanceof Error ? error.message : String(error) });
    }
  },
  save: async (childId, offsets) => {
    if (get().childId !== childId || get().saving) return;
    set({ saving: true, error: null });
    try {
      const saved = await roomApi.saveAccessoryOffsets(childId, offsets);
      if (get().childId !== childId) return;
      set({ offsets: saved, saving: false, error: null });
    } catch (error) {
      if (get().childId !== childId) return;
      set({ saving: false, error: error instanceof Error ? error.message : String(error) });
    }
  },
  reset: () => set(emptyState()),
}));

export function __resetPetAccessoryOffsetStoreForTests(): void {
  usePetAccessoryOffsetStore.setState(emptyState());
}
